import { useState } from 'react';
import { fmt, useT } from '../lib/i18n';
import { languageName, useVoices, VOICE_LANGS } from '../lib/languages';
import { updateSettings, useSettings, type VoiceLang } from '../lib/settings';
import { session, useSession } from '../lib/session';
import { cloudSupports, hasNaturalVoice, isEdge, itemLang, pickVoice, voiceShortName, type QueueItem } from '../lib/tts';

/**
 * Shows which language is being spoken right now and lets the streamer switch in one tap.
 * If a comment sounds odd, the wrong language is the usual reason.
 */
export function LangSwitch({ current, size = 'md' }: { current: QueueItem | null; size?: 'md' | 'lg' }) {
  const t = useT();
  const s = useSettings();
  const voices = useVoices();
  const sp = useSession().speech;
  const premium = sp.cloud === 'on' && s.premiumVoice;
  const speaking = itemLang(current, s);
  // A comment in Chinese, Tamil, ... is spoken in that language whatever is selected.
  const forced = current?.lang && current.lang.split('-')[0] !== s.voiceLang.split('-')[0] ? current.lang : null;

  const change = (value: VoiceLang) => {
    updateSettings({ voiceLang: value, voiceURI: null });
    // Replay the current comment in the new language so the difference is heard right away.
    if (current && !current.lang) session.tts.readAgain();
  };

  return (
    <span className={`langswitch langswitch--${size}`}>
      {forced && (
        <span className="langswitch__forced" title={t.detectedLanguage}>
          {languageName(forced)}
        </span>
      )}
      <label className="sr-only" htmlFor={`langsel-${size}`}>
        {t.changeVoiceLanguage}
      </label>
      <select id={`langsel-${size}`} className="langsel" value={s.voiceLang} onChange={(e) => change(e.target.value as VoiceLang)} title={fmt(t.speakingIn, { lang: languageName(speaking) })}>
        {VOICE_LANGS.map((l) => {
          const v = premium && cloudSupports(l.value) ? 'ElevenLabs' : voiceShortName(pickVoice(l.value, l.value === s.voiceLang ? s.voiceURI : null, voices));
          return (
            <option key={l.value} value={l.value}>
              {l.label}
              {v ? ` · ${v}` : ''}
            </option>
          );
        })}
      </select>
    </span>
  );
}

const DISMISS_KEY = 'bacachat.edgeHint.dismissed';

/**
 * Nudges people toward Microsoft Edge when this browser has no natural voice for the language.
 * Edge ships free neural voices for Indonesian (Gadis, Ardi) and Malay (Yasmin, Osman).
 */
export function EdgeHint({ dismissible = false, compact = false }: { dismissible?: boolean; compact?: boolean }) {
  const t = useT();
  const s = useSettings();
  const voices = useVoices();
  const cloudOn = useSession().speech.cloud === 'on' && s.premiumVoice;
  const [dismissed, setDismissed] = useState(() => {
    try {
      return dismissible && sessionStorage.getItem(DISMISS_KEY) === s.voiceLang;
    } catch {
      return false;
    }
  });
  // Wait for the voice list before judging; an empty list only means "not loaded yet".
  if (cloudOn || !voices.length || dismissed || isEdge() || hasNaturalVoice(s.voiceLang, voices)) return null;
  const text = fmt(t.edgeHint, { lang: languageName(s.voiceLang) });
  if (compact) return <p className="hint hint--left">{text}</p>;
  return (
    <div className="banner banner--info" role="note">
      <span>{text}</span>
      {dismissible && (
        <button
          type="button"
          className="btn"
          onClick={() => {
            try {
              sessionStorage.setItem(DISMISS_KEY, s.voiceLang);
            } catch {
              /* ignore */
            }
            setDismissed(true);
          }}
        >
          {t.dismiss}
        </button>
      )}
    </div>
  );
}
